import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customSoftwareEnquiriesAPI, getImageUrl } from '../../utils/api';
import { FaArrowLeft, FaDownload, FaEnvelope, FaPhoneAlt, FaGraduationCap, FaFileAlt } from 'react-icons/fa';

const AdminCustomSoftwareEnquiryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [enquiry, setEnquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusUpdating, setStatusUpdating] = useState(false);

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await customSoftwareEnquiriesAPI.getById(id);
      if (response.data?.success) {
        setEnquiry(response.data.data);
      } else {
        setError('Enquiry details not found');
      }
    } catch (err) {
      setError('Error loading enquiry details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setStatusUpdating(true);
      const response = await customSoftwareEnquiriesAPI.updateStatus(id, newStatus);
      if (response.data?.success) {
        setEnquiry({ ...enquiry, status: newStatus });
        alert('Status updated successfully');
      } else {
        alert('Failed to update status');
      }
    } catch (err) {
      console.error(err);
      alert('Error updating status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to permanently delete this enquiry?')) {
      try {
        setLoading(true);
        const response = await customSoftwareEnquiriesAPI.delete(id);
        if (response.data?.success) {
          alert('Enquiry deleted successfully');
          navigate('/admin/custom-software-enquiries');
        } else {
          alert('Failed to delete enquiry');
        }
      } catch (err) {
        console.error(err);
        alert('Error deleting enquiry');
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading enquiry details...</div>;
  if (error || !enquiry) return <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error || 'Enquiry not found'}</div>;

  return (
    <div style={{ padding: '30px', maxWidth: '1000px', margin: '0 auto' }}>
      <button 
        onClick={() => navigate('/admin/custom-software-enquiries')}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#10b981', fontWeight: '600', marginBottom: '25px', fontSize: '15px' }}
      >
        <FaArrowLeft /> Back to Software Project Enquiries
      </button>

      <div style={{ background: '#ffffff', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {/* Header ribbon */}
        <div style={{ background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)', padding: '30px', color: '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Software Enquiry - {enquiry.project_type}
            </span>
            <h1 style={{ fontSize: '28px', fontWeight: '800', marginTop: '10px', marginBottom: '6px' }}>{enquiry.project_title}</h1>
            <p style={{ margin: 0, opacity: 0.9 }}>Enquiry ID: <strong>{enquiry.enquiry_id}</strong></p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '14px', opacity: 0.8 }}>Submitted On</div>
            <div style={{ fontSize: '16px', fontWeight: '600' }}>{new Date(enquiry.created_at).toLocaleString()}</div>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '40px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '40px' }}>
            
            {/* Left Column */}
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', borderBottom: '2px solid #f1f5f9', paddingBottom: '10px', marginBottom: '20px' }}>
                Client Details
              </h2>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                <div>
                  <div style={{ fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}><FaFileAlt /> Client Name</div>
                  <div style={{ fontWeight: '500', color: '#334155' }}>{enquiry.full_name}</div>
                </div>
                <div>
                  <div style={{ fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}><FaEnvelope /> Email</div>
                  <div style={{ fontWeight: '500', color: '#334155' }}>{enquiry.email}</div>
                </div>
                <div>
                  <div style={{ fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}><FaPhoneAlt /> Phone</div>
                  <div style={{ fontWeight: '500', color: '#334155' }}>{enquiry.mobile}</div>
                </div>
                <div>
                  <div style={{ fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}><FaGraduationCap /> College</div>
                  <div style={{ fontWeight: '500', color: '#334155' }}>{enquiry.college || 'N/A'}</div>
                </div>
              </div>

              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', borderBottom: '2px solid #f1f5f9', paddingBottom: '10px', marginBottom: '20px' }}>
                Technology & Requirements
              </h2>
              
              <div style={{ marginBottom: '25px' }}>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Desired Technology</div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#10b981' }}>{enquiry.technology}</div>
              </div>

              <div style={{ background: '#f8fafc', padding: '25px', borderRadius: '8px', border: '1px solid #e2e8f0', color: '#475569', fontSize: '15px', lineHeight: '1.6' }}>
                <div style={{ fontWeight: '700', color: '#334155', marginBottom: '10px' }}>Project Specifications:</div>
                {enquiry.project_requirement}
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '25px', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '15px' }}>Enquiry Status</h3>
                
                <div style={{ marginBottom: '20px' }}>
                  <select
                    disabled={statusUpdating}
                    value={enquiry.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: '600', color: '#334155', background: '#ffffff' }}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Reviewed">Reviewed</option>
                    <option value="Shortlisted">Shortlisted</option>
                    <option value="Selected">Selected</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>
              </div>

              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '25px', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '15px' }}>Requirement Document</h3>
                
                {enquiry.requirement_document ? (
                  <a 
                    href={getImageUrl(enquiry.requirement_document)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: '#10b981', color: '#ffffff', textDecoration: 'none', padding: '12px', borderRadius: '8px', fontWeight: '600', transition: 'background-color 0.2s', width: '100%' }}
                  >
                    <FaDownload /> View Requirement File
                  </a>
                ) : (
                  <div style={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
                    <FaFileAlt /> No requirement document uploaded
                  </div>
                )}
              </div>

              <button 
                onClick={handleDelete}
                style={{ width: '100%', border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', padding: '12px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
              >
                Delete Enquiry
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCustomSoftwareEnquiryDetail;
